<template>
  <div>
    <PageAllow controller="ReturnOrders" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1 pe-2">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-spacer />
            <v-menu
              v-if="$permission.permissionAllow('ReturnOrders', 'returnOrdersPrint')"
              v-model="printBtnMenu"
              open-on-hover
              close-on-click
              close-on-content-click
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  dark
                  color="print"
                  class="ms-1 float-end"
                  @click.stop=""
                >
                  <v-icon x-small class="pe-1">fa-print</v-icon>
                  {{ $t("Print.batchPrint") }}
                  <v-icon x-small class="ps-1">
                    {{ printBtnMenu ? "fa-caret-up" : "fa-caret-down" }}
                  </v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-if="$permission.permissionAllow('ReturnOrders', 'returnOrdersPrint')"
                  @click.stop="doPrint('orderReturn')"
                >
                  <v-list-item-title>
                    {{ $t("ReturnOrder.actions.returnOrdersPrint") }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              v-if="
                !$route.path.startsWith('/return-orders-') &&
                $permission.permissionAllow('ReturnOrders', 'batchConfirm')
              "
              small
              color="warning"
              class="ms-1 float-end"
              @click="batchConfirmDialog = true"
            >
              <v-icon x-small class="pe-1">fa-align-justify</v-icon>
              {{ $t("ReturnOrder.batchConfirm") }}
            </v-btn>
            <v-btn
              v-if="
                $route.path.startsWith('/return-orders-approve') &&
                $permission.permissionAllow('ReturnOrders', 'batchApprove')
              "
              small
              color="warning"
              class="ms-1 float-end"
              @click="batchApproveDialog = true"
            >
              <v-icon x-small class="pe-1">fa-align-justify</v-icon>
              {{ $t("ReturnOrder.batchApprove") }}
            </v-btn>
            <v-btn
              v-if="
                $permission.permissionAllow('ReturnOrders', 'batchDetailExport')
              "
              small
              color="print"
              dark
              class="ms-1"
              @click.stop="batchExportExcel()"
            >
              <v-icon x-small class="pe-1">fa-file-excel</v-icon>
              {{ $t("ReturnOrder.batchExportExcel") }}
            </v-btn>
            <v-btn
              v-if="
                !$route.path.startsWith('/return-orders-') &&
                $permission.permissionAllow('ReturnOrders', 'add')
              "
              small
              color="primary"
              href="/#/return-order-new"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("ReturnOrder.ObjectName")]) }}
            </v-btn>
            <v-btn
              v-if="
                !$route.path.startsWith('/return-orders-') &&
                $permission.permissionAllow('ReturnOrders', 'add')
              "
              small
              color="primary"
              href="/#/return-order-apply"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("ReturnOrderApply.ObjectName") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="order_code"
                  v-model="options.order_code"
                  @text="orderCodeKeyword = $event"
                  :label="$t('ReturnOrder.Fields.order_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="ext_code"
                  v-model="options.ext_code"
                  @text="extCodeKeyword = $event"
                  :label="$t('ReturnOrder.Fields.ext_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="for_order_code"
                  v-model="options.for_order_code"
                  @text="forOrderCodeKeyword = $event"
                  :label="$t('ReturnOrder.Fields.order')"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="4"
                v-if="
                  $permission.permissionAllow('AllianceBusinesses', 'index')
                "
              >
                <SearcherAlliances v-model="alliancesSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterials v-model="materialSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="shop_keyword"
                  v-model="options.shop_keyword"
                  @text="shopKeyword = $event"
                  :label="$consts.shopName()"
                />
              </v-col>
<!--              <v-col cols="12" sm="6" md="4">-->
<!--                <SearcherShops v-model="shopsSelected" />-->
<!--              </v-col>-->
              <v-col cols="12" sm="6" md="4">
                <SearcherDistrict v-model="districtSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="memo"
                  v-model="options.memo"
                  @text="memoKeyword = $event"
                  :label="$t('ReturnOrder.Fields.memo')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.return_origin_flgs"
                  :items="$consts.return_order.return_origin_flgs"
                  :label="$t('ReturnOrder.Origin.title')"
                />
              </v-col>
              <v-col cols="12" class="d-flex mt-2">
                <SearcherStatus
                  v-if="!$route.path.startsWith('/return-orders-approve')"
                  v-model="options.status"
                  :items="$consts.return_order.status"
                  :label="$t('ReturnOrder.status.title')"
                />
                <v-sheet v-else class="transparent-background-color">
                  <div
                    class="caption grey--text input-chip-group-label"
                    :class="
                      $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                    "
                  >
                    {{ $t("ReturnOrder.grantedStatus") }}
                  </div>
                  <v-chip-group v-model="grantedStatus" column multiple>
                    <v-chip
                      small
                      label
                      filter
                      class="me-1"
                      color="accent"
                      :value="0"
                    >
                      {{ $t("Order.grantedStatus.confirmed") }}
                    </v-chip>
                    <v-chip
                      small
                      label
                      filter
                      class="me-1"
                      color="primary"
                      :value="1"
                    >
                      {{ $t("Order.grantedStatus.granted") }}
                    </v-chip>
                  </v-chip-group>
                </v-sheet>
                <v-spacer />
                <SearcherStatus
                  v-model="options.pay_flgs"
                  :items="$consts.bill.pay_flgs"
                  :label="$t('Bill.payFlgs.title')"
                />
                <v-spacer />
                <SearcherCancelFlgs v-model="options.cancel_flgs" />
                <v-spacer />
                <SearcherStatus
                  v-model="options.return_type"
                  :label="$t('ReturnOrder.Fields.type')"
                  :items="$consts.return_order.type"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="returnOrders"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.shop>
            {{ $consts.shopName() }}
          </template>
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>

          <template v-slot:item.order_code="{ item, index }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.order_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </span>
          </template>
          <template v-slot:item.shop.shop_name="{ item }">
            <span class="text-no-wrap">
              {{ item.shop.shop_name }}
            </span>
            <br />
            <span class="text-no-wrap caption secondary--text">
              {{ item.shop.shop_code }}
            </span>
          </template>
          <template v-slot:item.check_price="{ item }">
            {{ $t("Global.money") }}{{ item.check_price | formatMoney }}
          </template>
          <template v-slot:item.expect_return_price="{ item }">
            {{ $t("Global.money") }}{{ item.expect_return_price | formatMoney }}
          </template>
          <template v-slot:item.expect_date="{ item }">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="text-no-wrap caption secondary--text"
                  :class="
                    $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                  "
                >
                  {{ item.expect_date | formatDate }}
                </span>
              </template>
              <span>{{ item.expect_date | formatTime }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | returnOrderStatusColor"
              :text-color="item.status | returnOrderStatusTextColor"
              >{{ item.status | returnOrderStatusText }}
            </v-chip>
            <br />
            <v-chip
              v-if="item.cancel_flg || item.error_flg"
              x-small
              label
              outlined
              color="danger"
            >
              {{
                item.cancel_flg
                  ? $t("CRUD.cancelled")
                  : $t("Order.redeliverError")
              }}
            </v-chip>
          </template>
          <template v-slot:item.bill.pay_flg="{ item }">
            <span class="text-no-wrap">
              <v-chip
                small
                label
                :color="item.bill.pay_flg | billPayFlgsColor"
                :text-color="item.bill.pay_flg | billPayFlgsTextColor"
                >{{ item.bill.pay_flg | billPayFlgsText }}
              </v-chip>
              <v-chip
                small
                label
                class="d-none ms-1"
                :color="item.bill.status | billStatusColor"
                :text-color="item.bill.status | billStatusTextColor"
                >{{ item.bill.status | billStatusText }}
              </v-chip>
            </span>
          </template>
          <template v-slot:item.return_type="{ item }">
            <v-chip
              small
              label
              :color="item.return_type | returnOrderTypesColor"
              :text-color="item.return_type | returnOrderTypesTextColor"
              >{{ item.return_type | returnOrderTypesText }}
            </v-chip>
          </template>
          <template v-slot:item.origin_flg="{ item }">
            <v-chip
              small
              label
              :color="item.origin_flg | orderOriginFlgsColor"
              :text-color="item.origin_flg | orderOriginFlgsTextColor"
              >{{ item.origin_flg | orderOriginFlgsText }}
            </v-chip>
          </template>
          <template v-slot:item.account_memo="{ item }">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="d-block text-truncate"
                  style="max-width: 80px"
                  >{{ item.account_memo }}
                </span>
              </template>
              {{ item.account_memo }}
            </v-tooltip>
          </template>
          <template v-slot:item.actions="{ item }">
            <OrderActions
              :order="item"
              showView
              :showEdit="!$route.path.startsWith('/return-orders-')"
              :showConfirm="!$route.path.startsWith('/return-orders-')"
              :showRedeliver="!$route.path.startsWith('/return-orders-')"
              :showApprove="$route.path.startsWith('/return-orders-approve')"
              showCancel
              showPrint
              showExport
              @done="getOrdersFromAPI()"
            />
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <!-- 批量通过 -->
    <DialogAlert
      v-model="batchApproveDialog"
      :title="$t('$vuetify.warning')"
      :message="$t('ReturnOrder.messages.batchApproveAlert')"
      persistent
      useConfirm
      @confirm="
        batchApproveDialog = false;
        batchApproveInfoDialog = true;
        batchApprove(JSON.parse(JSON.stringify(options)));
      "
    />
    <BatchOperationInfoAlert
      v-model="batchApproveInfoDialog"
      :title="$t('$vuetify.alert')"
      :message="batchApproveInfo"
      persistent
      :closable="errorCount > 0"
    />

    <!-- 批量确认 -->
    <DialogAlert
      v-model="batchConfirmDialog"
      :title="$t('$vuetify.warning')"
      :message="$t('ReturnOrder.messages.batchConfirmAlert')"
      persistent
      useConfirm
      @confirm="
        batchConfirmDialog = false;
        batchConfirmInfoDialog = true;
        batchConfirm(JSON.parse(JSON.stringify(options)));
      "
    />
    <BatchOperationInfoAlert
      v-model="batchConfirmInfoDialog"
      :title="$t('$vuetify.alert')"
      :message="batchConfirmInfo"
      persistent
      :closable="errorCount > 0"
    />


  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
// import SearcherShops from "@/components/plugins/SearcherShops";
import SearcherMaterials from "@/components/plugins/SearcherMaterials";
import SearcherDistrict from "@/components/plugins/SearcherDistrict";
import SearcherAlliances from "@/components/plugins/SearcherAlliances";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import SearcherCancelFlgs from "@/components/plugins/SearcherCancelFlgs";
import OrderActions from "./plugins/Actions";
import ReturnOrders from "@/api/return_orders";
import DialogAlert from "@/components/plugins/DialogAlert";
import BatchOperationInfoAlert from "./plugins/BatchOperationInfoAlert";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  actual_time_from: "",
  actual_time_to: "",
  expect_time_from: "",
  expect_time_to: "",
  order_code: "",
  ext_code: "",
  for_order_code: "",
  origin_flgs: [],
  shop_ids: [],
  district_codes: [],
  alliance_business_ids: [],
  status: [1, 2, 3],
  pay_flgs: [1, 2, 3],
  cancel_flgs: [0],
  sortBy: [],
  sortDesc: [],
  material_ids: [],
  return_type: [1, 2, 3],
  memo:"",
  shop_keyword: "",
  return_origin_flgs: [1, 2, 3],
};


function getPageType() {
  return document.URL.endsWith("/return-orders-approve") ? 2 : 1;
}
function getOptionPrefix() {
  switch (getPageType()) {
    default:
    case 1:
      return "return-orders";
    case 2:
      return "return-orders-approve";
  }
}
function getOptionSelects() {
  switch (getPageType()) {
    default:
    case 1:
    case 2:
      return [
        "shopsSelected",
        "districtSelected",
        "materialSelected",
      ];
  }
}


export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherKeyword,
    // SearcherShops,
    SearcherDistrict,
    SearcherAlliances,
    SearcherStatus,
    SearcherCancelFlgs,
    OrderActions,
    SearcherMaterials,
    DialogAlert,
    BatchOperationInfoAlert,
  },

  computed: {
    items() {
      let items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.returnOrderManagement"),
          disabled: false,
          replace: true,
          href: "/return-orders",
        },
      ];
      if (this.$route.path.startsWith("/return-orders-approve")) {
        items.push({
          text: i18n.t("Nav.returnOrdersApprove"),
          disabled: true,
          href: "/return-orders-approve",
        });
      } else {
        items.push({
          text: i18n.t("Nav.returnOrders"),
          disabled: true,
          href: "/return-orders",
        });
      }
      return items;
    },
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("ReturnOrder.Fields.created_time"),
      },
      {
        key: "actual",
        text: i18n.t("ReturnOrder.Fields.actual_time"),
      },
      {
        key: "expect",
        text: i18n.t("ReturnOrder.Fields.expect_date"),
      },
    ],
    headers() {
      return [
        {
          text:
            i18n.t("ReturnOrder.Fields.order_code") +
            " / " +
            i18n.t("ReturnOrder.Fields.created_time"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "order_code",
        },
        {
          text: i18n.t("ReturnOrder.Fields.shop", [this.$consts.shopName()]),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "shop.shop_name",
        },
        {
          text: i18n.t("ReturnOrder.Fields.check_price"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "check_price",
        },
        {
          text: i18n.t("ReturnOrder.Fields.expect_return_price"),
          align: "end",
          class: "text-no-wrap",
          sortable: false,
          value: "expect_return_price",
        },
        {
          text: i18n.t("ReturnOrder.Fields.memo"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "memo",
        },
        {
          text: i18n.t("ReturnOrder.Fields.status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("ReturnOrder.Fields.type"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "return_type",
        },
        {
          text: i18n.t("ReturnOrder.Fields.bill_pay_flgs"),
          // + " / " + i18n.t("Order.Fields.bill_status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "bill.pay_flg",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  mounted() {
    this.$commonUtils
      .loadOptions(getOptionPrefix(), getOptionSelects())
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "shopsSelected":
                this.shopsSelected = selected.value;
                break;
              case "districtSelected":
                this.districtSelected = selected.value;
                break;
              case "materialSelected":
                this.materialSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  data: () => ({
    orderCodeKeyword: "",
    extCodeKeyword: "",
    memoKeyword: "",
    shopKeyword: "",
    forOrderCodeKeyword: "",
    shopsSelected: [],
    districtSelected: null,
    alliancesSelected: [],
    grantedStatus: [0],
    options: JSON.parse(
      sessionStorage.getItem(getOptionPrefix() + "-options")
        ? sessionStorage.getItem(getOptionPrefix() + "-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    returnOrders: [],
    materialSelected: [],
    batchApproveDialog: false,
    batchApproveInfoDialog: false,
    batchApproveInfo: i18n.t("ReturnOrder.messages.batchApproveInfo", [0, 0]),
    batchApproveErrorDialog: false,
    batchApproveErrorData: [],
    batchConfirmDialog: false,
    batchConfirmInfoDialog: false,
    batchConfirmInfo: i18n.t("ReturnOrder.messages.batchConfirmInfo", [0, 0]),
    batchConfirmErrorDialog: false,
    batchConfirmErrorData: [],
    successCount: 0,
    errorCount: 0,
    alreadyApprovedOrderLength: 0,
    alreadyConfirmedOrderLength: 0,
    error_order_id: [],
    printBtnMenu: false,
  }),

  watch: {
    $route: {
      handler() {
        this.pageType = getPageType();
        this.$commonUtils
          .loadOptions(getOptionPrefix(), getOptionSelects()).then(
          function (data) {
            if (data.options) {
              this.options = data.options;
            } else this.resetSearch();
            data.selecteds.forEach((selected) => {
              switch (selected.key) {
                case "shopsSelected":
                  this.shopsSelected = selected.value;
                  break;
                case "districtSelected":
                  this.districtSelected = selected.value;
                  break;
                case "materialSelected":
                  this.materialSelected = selected.value;
                  break;
              }
            });
          }.bind(this)
        );
      },
    },
    grantedStatus: {
      handler() {
        this.getOrdersFromAPI();
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getOrdersFromAPI();
        sessionStorage.setItem(
          getOptionPrefix() + "-options",
          JSON.stringify(this.options)
        );
      },
    },
    shopsSelected: {
      handler() {
        this.options.shop_ids = this.shopsSelected
          ? this.shopsSelected.map((alliance) => {
              return alliance.id;
            })
          : [];
        sessionStorage.setItem(
          getOptionPrefix() + "-shopsSelected",
          JSON.stringify(this.shopsSelected)
        );
      },
    },
    materialSelected: {
      handler() {
        this.options.material_ids = this.materialSelected
          ? this.materialSelected.map((e) => {
              return e.id;
            })
          : [];
        sessionStorage.setItem(
          getOptionPrefix() + "-materialSelected",
          JSON.stringify(this.materialSelected)
        );
      },
    },
    districtSelected: {
      handler() {
        this.options.district_codes = this.districtSelected
          ? [this.districtSelected.code]
          : [];
        sessionStorage.setItem(
          getOptionPrefix() + "-districtSelected",
          JSON.stringify(this.districtSelected)
        );
      },
    },
    alliancesSelected: {
      handler() {
        this.options.alliance_business_ids = this.alliancesSelected
          ? this.alliancesSelected.map((alliance) => {
              return alliance.id;
            })
          : [];
        sessionStorage.setItem(
          getOptionPrefix() + "-alliancesSelected",
          JSON.stringify(this.alliancesSelected)
        );
      },
    },
  },

  methods: {
    resetSearch() {
      this.shopsSelected = [];
      this.materialSelected = [];
      this.districtSelected = null;
      this.alliancesSelected = [];
      this.grantedStatus = [0];
      this.forOrderCodeKeyword = "";
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [
        this.$refs.order_code,
        this.$refs.ext_code,
        this.$refs.for_order_code,
        this.$refs.memo,
        this.$refs.shop_keyword,
      ].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        order_code: this.orderCodeKeyword,
        ext_code: this.extCodeKeyword,
        memo: this.memoKeyword,
        shop_keyword: this.shopKeyword,
        for_order_code: this.forOrderCodeKeyword,
      });
    },
    getOrdersFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      if (
        getPageType() == 2 &&
        this.grantedStatus &&
        Array.isArray(this.grantedStatus)
      ) {
        var status = [];
        this.grantedStatus.forEach((value) => {
          switch (value) {
            case 0:
              status.push(2);
              break;
            case 1:
              status.push(3, 4);
              break;
          }
        });
        options.status = status;
      }
      ReturnOrders.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.returnOrders = response.data.return_orders;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    batchExportExcel() {
      var options = JSON.parse(JSON.stringify(this.options));
      if (
        getPageType() == 2 &&
        this.grantedStatus &&
        Array.isArray(this.grantedStatus)
      ) {
        var status = [];
        this.grantedStatus.forEach((value) => {
          switch (value) {
            case 0:
              status.push(2);
              break;
            case 1:
              status.push(3, 4);
              break;
          }
        });
        options.status = status;
      }
      ReturnOrders.batcEexportExcel(options);
    },
    batchApprove(options) {
      if (this.error_order_id.length) {
        options.no_order_ids = this.error_order_id;
      }
      ReturnOrders.batchApprove(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            if (response.data.approve_flg) {
              ++this.successCount;
            } else {
              this.error_order_id.push(response.data.order_id);
              ++this.errorCount;
              // this.batchApproveErrorData.push(response.data.data[0]);
              // this.batchApproveErrorDialog = true;
            }
          } else {
            if (response.data.error.code != "ERR333") {
              this.error_order_id.push(response.data.order_id);
              ++this.errorCount;
              if (response.data.error.code == "ERR668") {
                this.batchApproveErrorData.push(response.data.data[0]);
                this.batchApproveErrorDialog = true;
              }
            } else {
              this.getOrdersFromAPI();
              options = null;
              this.error_order_id = [];
              this.successCount = 0;
              this.errorCount = 0;
              this.alreadyApprovedOrderLength = 0;
              this.batchApproveInfoDialog = false;
              this.$store.commit("refreshToDos", true);
            }
          }
          //实时计数
          this.batchApproveInfo = i18n.t("ReturnOrder.messages.batchApproveInfo", [
            this.successCount,
            this.errorCount,
          ]);
          ++this.alreadyApprovedOrderLength; //计已审批的单子总数
          if (response.data.status.isOK() || response.data.error.code != "ERR333"
          ) {
            //回调
            this.batchApprove(options);
          }
        }.bind(this)
      );
    },
    batchConfirm(options){
      if (this.error_order_id.length) {
        options.no_order_ids = this.error_order_id;
      }
      ReturnOrders.batchConfirm(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            if (response.data.confirm_flg) {
              ++this.successCount;
            } else {
              this.error_order_id.push(response.data.order_id);
              ++this.errorCount;
            }
          } else {
            if (response.data.error.code != "ERR333") {
              this.error_order_id.push(response.data.order_id);
              ++this.errorCount;
              if (response.data.error.code == "ERR668") {
                this.batchConfirmErrorData.push(response.data.data[0]);
                this.batchConfirmErrorDialog = true;
              }
            } else {
              this.getOrdersFromAPI();
              options = null;
              this.error_order_id = [];
              this.successCount = 0;
              this.errorCount = 0;
              this.alreadyConfirmedOrderLength = 0;
              this.batchConfirmInfoDialog = false;
              this.$store.commit("refreshToDos", true);
            }
          }
          //实时计数
          this.batchConfirmInfo = i18n.t("ReturnOrder.messages.batchConfirmInfo", [
            this.successCount,
            this.errorCount,
          ]);
          ++this.alreadyConfirmedOrderLength; //计已确认的单子总数
          if (response.data.status.isOK() || response.data.error.code != "ERR333"
          ) {
            //回调
            this.batchConfirm(options);
          }
        }.bind(this)
      );
    },
    doPrint(mode) {
      this.$permission.checkAccountExpired().then(
        function () {
          let options = JSON.parse(JSON.stringify(this.options));
          options.page = 1;
          options.itemsPerPage = this.$consts.limitAll;
          switch (mode) {
            case "orderReturn":
              this.$router.push({
                path: "/print/return-orders-print",
                query: options,
              });
              break;
          }
        }.bind(this)
      );
    },
  },
};
</script>
